import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Beaker, Leaf, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { References } from "@/components/References";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import heroAsset from "@/assets/blog/leg-cramps.jpg.asset.json";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";

const URL = "https://truwe.in/blog/leg-cramps-at-night-causes-india";
const TITLE = "Leg Cramps at Night: Causes, Fixes & When to See a Doctor (India, 2026) | TruWe";

export const Route = createFileRoute("/blog/leg-cramps-at-night-causes-india")({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: "leg-cramps-at-night-causes-india",
      title: TITLE,
      description:
        "Waking up with calf cramps? The 6 real causes of nocturnal leg cramps in Indian adults magnesium, hydration, medications, circulation and what actually stops them.",
      ogTitle: "Leg Cramps at Night: Causes and Fixes",
      ogDescription:
        "Nocturnal calf cramps aren't random. Here are the 6 causes to check, in order, and when a cramp is a red flag.",
      headline: "Why Your Legs Cramp at Night. Causes, Fixes & Red Flags",
      breadcrumbName: "Leg Cramps at Night (India Guide)",
      datePublished: "2026-07-04",
      dateModified: "2026-07-25",
      ogImage: `https://truwe.in${heroAsset.url}`,
      faqs: [
        {
          q: "What's the fastest way to stop a cramp mid-attack?",
          a: "Stand up and pull the toes toward the shin (dorsiflex), this stretches the cramped calf and usually breaks the spasm within 30 seconds. Follow with a warm compress.",
        },
        {
          q: "Is magnesium the answer for nocturnal leg cramps?",
          a: "For most people, yes, magnesium bisglycinate at 200-300 mg/day resolves cramps in 2-3 weeks. But cramps can also come from dehydration, statins, diuretics or circulation problems; magnesium isn't a universal fix.",
        },
        {
          q: "When should I see a doctor about cramps?",
          a: "Cramps that come with swelling, discolouration, warmth, or that occur while walking (claudication) can indicate vascular disease and need a doctor. Cramps with new medications should be reviewed too.",
        },
        {
          q: "Do bananas actually help?",
          a: "Marginally. Bananas provide potassium, which matters, but the main deficiency is usually magnesium. Kaddu ke beej (pumpkin seeds) and almonds do more.",
        },
      ],
    }),
});

const CAUSES = [
  {
    name: "Low magnesium",
    note: "The single most common cause. Magnesium relaxes muscle after contraction; without it, the fibre stays partially locked. Fix with 200-300 mg bisglycinate at bedtime.",
  },
  {
    name: "Dehydration + electrolyte imbalance",
    note: "Especially in Indian summers or after heavy sweat sessions. Replace with water + a pinch of sea salt + a squeeze of lemon; don't just drink plain water.",
  },
  {
    name: "Medications",
    note: "Statins, diuretics (thiazides, furosemide), some blood pressure meds and PPIs (omeprazole) all strip magnesium or interfere with muscle chemistry.",
  },
  {
    name: "Standing / sitting posture all day",
    note: "Shortened calves from long hours in shoes or a chair cramp more easily. A 90-second wall-calf stretch morning and night resets the length.",
  },
  {
    name: "Peripheral circulation issues",
    note: "If cramps also happen while walking (calf pain that eases with rest), the cause may be peripheral arterial disease, see a doctor. Not a supplement problem.",
  },
  {
    name: "Pregnancy",
    note: "Very common in the 2nd and 3rd trimester as the growing baby draws magnesium and calcium. Bisglycinate + calcium is standard obstetric advice; check with your OB.",
  },
] as const;

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Sleep & Recovery
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            Why your legs cramp at night, causes, fixes and red flags
          </h1>
          <BlogByline date="2026-07-04" readingTime="8 min read" />
          <div className="mt-4">
            <ReadWithLlm title={TITLE} url={URL} kind="article" />
          </div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Nocturnal leg cramps affect roughly 1 in 3 adults over 50 and are strikingly
            common in Indian women through pregnancy and perimenopause. They're not
            random, there's almost always a mechanical or biochemical reason. Here's
            how to work through them in order.
          </p>
        <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={heroAsset.url}
              alt="Leg cramps at night, causes and calf massage relief"
              width={1200}
              height={630}
              loading="eager"
              className="h-auto w-full"
            />
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            What a cramp actually is
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            A cramp is an involuntary, sustained contraction, the muscle fibres receive
            a signal to shorten and can't release. Two systems have to work for release:
            <strong> calcium clears out of the muscle</strong> and <strong>magnesium
            allows the actin-myosin bond to unstick</strong>. When either is short, the
            fibre stays locked.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            The 6 real causes, check in this order
          </h2>
          <div className="mt-5 space-y-4">
            {CAUSES.map((row, i) => (
              <div key={row.name} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <Beaker className="h-4 w-4 text-brand" />
                  <h3 className="font-display text-lg font-semibold text-brand-deep">
                    {i + 1}. {row.name}
                  </h3>
                </div>
                <p className="mt-2 text-sm text-foreground/80">{row.note}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            The 3-week protocol that works for most people
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Magnesium bisglycinate 200-300 mg</strong>, one hour before bed.
            </li>
            <li>
              <strong>Wall calf-stretch, 90 seconds each leg</strong>, morning and
              night. This is doing more than most people expect.
            </li>
            <li>
              <strong>Salted water at dinner</strong> if you sweat heavily during the day.
            </li>
            <li>
              <strong>Warm bath or a heat pack</strong> on the calves in the hour before
              bed, reduces muscle spindle firing.
            </li>
            <li>
              <strong>Reduce alcohol and refined carbs</strong> both drain magnesium
              overnight.
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            Red flags, see a doctor
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>Cramp with swelling, redness or a warm calf, rule out DVT immediately.</li>
            <li>Calf pain while walking that eases with rest, possible PAD.</li>
            <li>New cramps after starting a statin, diuretic or PPI, review dose with your doctor.</li>
            <li>Weakness that outlasts the cramp, or twitching all over, needs a neuro assessment.</li>
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-14 rounded-3xl border border-brand/15 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5 p-8 md:p-10">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand">
              <Sparkles className="h-4 w-4" /> The TruWe formulation
            </div>
            <h2 className="mt-3 font-display text-2xl font-bold text-brand-deep md:text-3xl">
              Mag Absorb Pro, the five forms that stop cramps
            </h2>
            <p className="mt-3 text-base text-foreground/85">
              Cramp-relevant magnesium isn't oxide, it's <strong>bisglycinate for
              nerve-muscle relaxation and chloride/aspartate for cellular ATP</strong>,
              which is what an exhausted muscle needs to release. Aquamin® adds the
              trace mineral matrix.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                { icon: Leaf, label: "Bedtime-friendly" },
                { icon: ShieldCheck, label: "Third-party tested" },
                { icon: Beaker, label: "Research-backed doses" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 rounded-xl bg-background/70 px-3 py-3 text-sm text-foreground/85 ring-1 ring-border"
                >
                  <Icon className="h-4 w-4 text-brand" />
                  {label}
                </div>
              ))}
            </div>
            <Link
              to="/products/$handle"
              params={{ handle: "mag-absorb-pro" }}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-luxury transition hover:bg-brand-deep"
            >
              Explore Mag Absorb Pro <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="magnesium" />
          <ClusterLinks cluster="magnesium" />
          <AuthorityFlow cluster="magnesium" />
          <References slug="leg-cramps-at-night-causes-india" />
          <RelatedReading articles={relatedArticles("leg-cramps-at-night-causes-india")} />
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            Educational only. Red-flag cramps (swelling, walking pain, weakness) need a
            doctor, not a supplement.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
